import React from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Components
import AdminLogin from './components/auth/AdminLogin';
import EmployeeLogin from './components/auth/EmployeeLogin';
import ClientLogin from './components/auth/ClientLogin';
import VendorLogin from './components/auth/VendorLogin';

// Dashboard & Layout
import Dashboard from './components/Dashboard';
import MainLayout from './components/layout/MainLayout';

// Module Pages (to be created)
import Departments from './pages/hrm/Departments';
import Designations from './pages/hrm/Designations';
import Employees from './pages/hrm/Employees';
import EmployeeCategories from './pages/hrm/EmployeeCategories';
import Attendances from './pages/hrm/Attendances';
import LeaveApplications from './pages/hrm/LeaveApplications';
import LeaveTypes from './pages/hrm/LeaveTypes';
import LeavePolicies from './pages/hrm/LeavePolicies';
import SalaryStructures from './pages/hrm/SalaryStructures';
import LoanApplications from './pages/hrm/LoanApplications';
import Movements from './pages/hrm/Movements';
import Holidays from './pages/hrm/Holidays';
import Settings from './pages/hrm/Settings';
import EmployeeDocuments from './pages/hrm/EmployeeDocuments';
import EmployeeEmergencyContacts from './pages/hrm/EmployeeEmergencyContacts';
import EmployeeExperiences from './pages/hrm/EmployeeExperiences';
import EmployeeQualifications from './pages/hrm/EmployeeQualifications';
import EmployeeProfile from './pages/hrm/EmployeeProfile';

import ChartOfAccounts from './pages/finance/ChartOfAccounts';
import BankAccounts from './pages/finance/BankAccounts';
import JournalEntries from './pages/finance/JournalEntries';
import OpeningBalanceSettings from './pages/finance/FinanceSettings';
import CompanySettings from './pages/settings/CompanySettings';

import Stores from './pages/inventory/Stores';
import Products from './pages/inventory/Products';
import InventoryStocks from './pages/inventory/InventoryStocks';

import Clients from './pages/crm/Clients';
import Projects from './pages/crm/Projects';
import Lifts from './pages/crm/Lifts';
import Invoices from './pages/crm/Invoices';
import GenerateInvoice from './pages/crm/GenerateInvoice';
import LiftBrands from './pages/crm/LiftBrands';
import LiftTypes from './pages/crm/LiftTypes';

import Vendors from './pages/vendor/Vendors';

function LoginSelection() {
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center">Select Login</h2>
                <div className="space-y-4">
                    <Link to="/" className="block w-full py-2 px-4 text-center bg-gray-600 hover:bg-gray-700 text-white font-bold rounded">
                        Admin Login
                    </Link>
                    <Link to="/employee/login" className="block w-full py-2 px-4 text-center bg-green-600 hover:bg-green-700 text-white font-bold rounded">
                        Employee Login
                    </Link>
                    <Link to="/client/login" className="block w-full py-2 px-4 text-center bg-yellow-600 hover:bg-yellow-700 text-white font-bold rounded">
                        Client Login
                    </Link>
                    <Link to="/vendor/login" className="block w-full py-2 px-4 text-center bg-red-600 hover:bg-red-700 text-white font-bold rounded">
                        Vendor Login
                    </Link>
                </div>
            </div>
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <Router>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<AdminLogin />} />
                        <Route path="/logins" element={<LoginSelection />} />
                        <Route path="/employee/login" element={<EmployeeLogin />} />
                        <Route path="/client/login" element={<ClientLogin />} />
                        <Route path="/vendor/login" element={<VendorLogin />} />

                        {/* Protected Routes - Admin Only */}
                        <Route path="/dashboard" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Dashboard />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* HRM Routes */}
                        <Route path="/hrm/departments" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Departments />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/designations" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Designations />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employees" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Employees />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employee-categories" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeCategories />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/attendances" element={
                            <ProtectedRoute allowedRoles={['admin', 'employee']}>
                                <MainLayout>
                                    <Attendances />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/leave-applications" element={
                            <ProtectedRoute allowedRoles={['admin', 'employee']}>
                                <MainLayout>
                                    <LeaveApplications />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/leave-types" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <LeaveTypes />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/leave-policies" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <LeavePolicies />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/salary-structures" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <SalaryStructures />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/loan-applications" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <LoanApplications />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/movements" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Movements />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/holidays" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Holidays />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/settings" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Settings />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employee-documents" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeDocuments />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employee-emergency-contacts" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeEmergencyContacts />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employee-experiences" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeExperiences />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employee-qualifications" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeQualifications />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/hrm/employees/:id/profile" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <EmployeeProfile />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* Finance Routes */}
                        <Route path="/finance/chart-of-accounts" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <ChartOfAccounts />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/finance/bank-accounts" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <BankAccounts />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/finance/journal-entries" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <JournalEntries />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/finance/settings" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <OpeningBalanceSettings />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* Company Settings Route */}
                        <Route path="/settings/company" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <CompanySettings />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* Inventory Routes */}
                        <Route path="/inventory/stores" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Stores />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/inventory/products" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Products />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/inventory/stocks" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <InventoryStocks />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* CRM Routes */}
                        <Route path="/crm/clients" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Clients />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/projects" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Projects />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/lifts" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Lifts />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/invoices" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Invoices />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/generate-invoice" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <GenerateInvoice />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/lift-brands" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <LiftBrands />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                        <Route path="/crm/lift-types" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <LiftTypes />
                                </MainLayout>
                            </ProtectedRoute>
                        } />

                        {/* Vendor Routes */}
                        <Route path="/vendors" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <MainLayout>
                                    <Vendors />
                                </MainLayout>
                            </ProtectedRoute>
                        } />
                    </Routes>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
